import { VideoType } from 'helpers/Constants/Constant';

const YOUTUBE_BASE_URL = 'https://www.youtube.com';
const YOUTUBE_ID_REGEXP = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;

const applyYouTubeBaseUrl = (url: string, youTubeBaseURL?: string): string => {
  const baseUrl = youTubeBaseURL?.trim()?.replace(/\/+$/, '');
  if (!baseUrl) return url;
  return url.replace(YOUTUBE_BASE_URL, baseUrl);
};

// Utility to extract YouTube video ID
export const getYouTubeId = (url: string): string | null => {
  const match = url.match(YOUTUBE_ID_REGEXP);
  return match && match[2].length === 11 ? match[2] : null;
};

// Utility to extract Vimeo video ID
export const getVimeoId = (url: string): string | null => {
  const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  return match ? match[1] : null;
};

/** Platform video ID used for analytics metadata */
export const getVideoId = (url: string): string => {
  const platform = getVideoPlatform(url);
  if (platform === 'youtube') return getYouTubeId(url) || '';
  if (platform === 'vimeo') return getVimeoId(url) || '';
  return '';
};

/**
 * Appends player API query params so parent-page listeners can track playback.
 * Safe to call with URLs that already include query strings (e.g. autoplay).
 */
export const withVideoPlayerApi = (embedUrl: string, platform: VideoType): string => {
  if (!embedUrl || !platform) return embedUrl;

  try {
    const url = new URL(embedUrl);
    if (platform === 'youtube') {
      // enablejsapi allows the YouTube IFrame API to bind to the embed.
      // Intentionally omit `origin` here to keep SSR/client markup identical.
      url.searchParams.set('enablejsapi', '1');
    } else if (platform === 'vimeo') {
      url.searchParams.set('api', '1');
      // Vimeo loads Google Cast on init; Cast constructs PresentationRequest and
      // throws if any ancestor frame is sandboxed (e.g. Sitecore Pages).
      url.searchParams.set('chromecast', '0');
    }
    return url.toString();
  } catch {
    return embedUrl;
  }
};

// Check video type.
export const getVideoPlatform = (url: string): VideoType => {
  try {
    const parsedUrl = new URL(url);
    const hostname = parsedUrl.hostname.toLowerCase();

    if (hostname.includes('youtube.com') || hostname.includes('youtu.be')) {
      return 'youtube';
    }

    if (hostname.includes('vimeo.com')) {
      return 'vimeo';
    }

    return undefined;
  } catch (e) {
    console.log(e);
    return undefined;
  }
};

export const getYouTubeEmbedUrl = (url: string, youTubeBaseURL?: string): string => {
  const videoId = getYouTubeId(url);
  if (!videoId) return url;

  const embedUrl = `${YOUTUBE_BASE_URL}/embed/${videoId}`;
  return applyYouTubeBaseUrl(embedUrl, youTubeBaseURL);
};

export const getVimeoUrl = (url: string): string => {
  const videoId = getVimeoId(url || '');
  return `https://player.vimeo.com/video/${videoId}?chromecast=0`;
};

// Enhanced version for modal with autoplay
export const getYouTubeEmbedUrlWithAutoplay = (url: string, youTubeBaseURL?: string): string => {
  const videoId = getYouTubeId(url);
  if (!videoId) return url;

  const embedUrl = `${YOUTUBE_BASE_URL}/embed/${videoId}?autoplay=1`;
  return applyYouTubeBaseUrl(embedUrl, youTubeBaseURL);
};

export const getVimeoUrlWithAutoplay = (url: string): string => {
  const videoId = getVimeoId(url || '');
  return `https://player.vimeo.com/video/${videoId}?autoplay=1&chromecast=0`;
};

// Generic function to get processed video URL with autoplay
export const getProcessedVideoUrl = (url: string, youTubeBaseURL?: string): string => {
  if (!url) return '';

  const platform = getVideoPlatform(url);

  switch (platform) {
    case 'youtube':
      return getYouTubeEmbedUrlWithAutoplay(url, youTubeBaseURL);
    case 'vimeo':
      return getVimeoUrlWithAutoplay(url);
    default:
      return url;
  }
};
